import React, { useRef } from 'react'

import useDeepEqualsMemoValue from 'v2/ui/utils/useDeepEqualsMemoValue'

import { useLayoutEditorDnDContext } from './hooks/useLayoutEditorDnDContext'
import { useLayoutEditorContext } from './useLayoutEditorContext'
import {
    LayoutEditorWidgetAreaContext,
    useLayoutEditorWidgetAreaContext,
} from './useLayoutEditorWidgetAreaContext'
import { useLayoutEditorWidgetContext } from './useLayoutEditorWidgetContext'
import { getWidgetAreaAtPath } from './utils'

type LayoutEditorWidgetAreaContextProviderProps = {
    id: string
}

export const LayoutEditorWidgetAreaContextProvider: React.FC<
    LayoutEditorWidgetAreaContextProviderProps
> = ({ id, children }) => {
    const { schema, view, isEditing } = useLayoutEditorContext()
    const { widget } = useLayoutEditorWidgetContext() ?? {}
    const { path = [] } = useLayoutEditorWidgetAreaContext() || {}

    // Widget area contexts can be nested, to calculate the parent ID path.
    const newPath = widget ? [...path, widget.id, id] : [...path, id]
    const newPathRef = useRef(newPath)
    newPathRef.current = newPath

    const childrenWidgets = useDeepEqualsMemoValue(getWidgetAreaAtPath(newPath, view?.layout) ?? [])

    const isRoot = newPath.length === 1

    const { activeDragWidget, destinationDragTarget } = useLayoutEditorDnDContext()

    let isDraggingAboveIdx: number | undefined = undefined
    let isDraggingBelowIdx: number | undefined = undefined

    const destinationDragTargetPathStr = destinationDragTarget?.path.join(',')
    const newPathStr = newPath.join(',')
    // We are dragging this widget to this widget area.
    if (
        !!activeDragWidget &&
        destinationDragTarget &&
        destinationDragTargetPathStr === newPathStr
    ) {
        // Insert the widget at the destination index. The destination index could be out of bounds, so we need to clamp it.
        const activeIdx = childrenWidgets.findIndex((w) => w.id === activeDragWidget.id)
        const newIdx = Math.min(destinationDragTarget.idx, childrenWidgets.length) // Clamp to bounds.
        if (activeIdx >= 0 && activeIdx !== newIdx) {
            isDraggingAboveIdx = newIdx < activeIdx ? newIdx : newIdx + 1
        } else {
            isDraggingAboveIdx = newIdx
        }

        if (isDraggingAboveIdx >= childrenWidgets.length && childrenWidgets.length > 0) {
            isDraggingBelowIdx = childrenWidgets.length - 1
            isDraggingAboveIdx = undefined
        }
    }

    const value = useDeepEqualsMemoValue({
        path: newPathRef.current,
        children: childrenWidgets,
        editorSchema: schema,
        isEditing,
        isRoot,
        isDraggingAboveIdx,
        isDraggingBelowIdx,
    })

    return (
        <LayoutEditorWidgetAreaContext.Provider value={value}>
            {children}
        </LayoutEditorWidgetAreaContext.Provider>
    )
}
